import { Component, Input, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
  catchError,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { UserService } from '../../../services/user.service';
import { AuthService } from '../../../services/auth.service';
import { PopupService } from '../../../services/popup.service';
import { LoaderService } from '../../../services/loader.service';
import { base64ToFile } from '../../../helpers/base64ToFile';
import { fileToBase64 } from '../../../helpers/fileToBase64';
import { BaseComponent } from '../../base/base.component';
import { ImageCropPopupComponent } from '../../popups/image-crop-popup/image-crop-popup.component';
import { HttpErrorResponse } from '@angular/common/http';
import { capitalize } from '../../../helpers/capitalize';

@Component({
  selector: 'app-pick-name',
  templateUrl: './pick-name.component.html',
  styleUrls: ['./pick-name.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ImageCropPopupComponent,
    RouterModule,
  ],
})
export class PickNameComponent extends BaseComponent implements OnInit {
  @Input() isMobile = true;
  public nameFormGroup: UntypedFormGroup;
  public chosenPhoto?: string;
  public showImageCropper = false;
  public showImageInput: false;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private userService: UserService,
    private authService: AuthService,
    private popupService: PopupService,
    private loaderService: LoaderService
  ) {
    super();
  }

  ngOnInit() {
    this.nameFormGroup = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', Validators.email],
      username: ['', Validators.required],
    });
  }

  public saveUser(): void {
    this.loaderService.show();
    this.autocapitalizeNames();

    const withImage = this.chosenPhoto
      ? from(base64ToFile(this.chosenPhoto, 'img', 'image')).pipe(
          switchMap((file: File) => this.userService.uploadImage(file)),
          tap(([url]) => this.patchUrlInForm(url)),
          catchError((err: HttpErrorResponse) => this.handleError(err))
        )
      : of({});

    withImage
      .pipe(
        switchMap(() =>
          this.authService.registration(
            this.authService.token,
            this.nameFormGroup.value
          )
        ),
        switchMap(() => this.popupService.showWelcomePagePopup().action$),
        takeUntil(this.unsubscribe$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe(() =>
        this.authService.redirectUrl
          ? this.router.navigateByUrl(this.authService.redirectUrl)
          : this.router.navigate(['/my/builder/intro'], {
              queryParams: {
                redirectTo: '/',
              },
            })
      );
  }

  public openCropImagePopup(): void {
    this.popupService
      .showCropImagePopup(this.chosenPhoto)
      .crop$.pipe(
        tap((croppedImage) => (this.chosenPhoto = croppedImage)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  private showCropImageView() {
    this.showImageCropper = true;
  }

  public choseImageHandler(croppedImage: string): void {
    this.chosenPhoto = croppedImage;
    this.showImageCropper = false;
  }

  public async choseImage(e) {
    if (e.target.files.length > 0) {
      const base64 = await fileToBase64(e.target.files[0]);
      if (base64) {
        this.chosenPhoto = base64 as string;
        this.isMobile ? this.openCropImagePopup() : this.showCropImageView();
      }
    }
  }

  private patchUrlInForm(url: string) {
    const image = url.slice(0, url.indexOf('?'));
    this.nameFormGroup.patchValue({ image });
  }

  private handleError(err): Observable<never> {
    this.popupService.showErrorResponsePopup(err.message);
    throw new Error(err);
  }

  private autocapitalizeNames(): void {
    const firstName = this.nameFormGroup.get('firstName');
    const lastName = this.nameFormGroup.get('lastName');
    firstName.setValue(capitalize(firstName.value));
    lastName.setValue(capitalize(lastName.value));
  }
}
